import {
  createContractRequestToDto,
  mapContractDto,
  mapListContractsResponseDto,
  seatMutationRequestToDto,
  updateContractRequestToDto,
  type Contract,
  type ContractDto,
  type CreateContractRequest,
  type ListContractsResponseDto,
  type SeatMutationRequest,
  type UpdateContractRequest,
} from '@/types';

import type { ContractService } from '../contractService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`ContractService`(R106 / docs/65)
 * -----------------------------------------------------------------------------
 * 全走 `httpRequestWithRefresh`(cookie auth + CSRF + `X-Current-Group` 自動注入 + 401 refresh)。
 * BaseResponse `result` unwrap;單筆合約 = `ContractDto`,list = `{ contracts: ContractDto[] }`。
 * 寫操作(POST/PATCH/DELETE)自動帶 CSRF —— **絕不可**把 `/admin/contracts` / `/contracts/*`
 * 加進 exempt 表。
 */

const PATH = {
  adminContracts: '/api/admin/contracts',
  adminContract: (sid: string) => `/api/admin/contracts/${encodeURIComponent(sid)}`,
  mine: '/api/contracts/mine',
  mineSeats: '/api/contracts/mine/seats',
} as const;

export function createHttpContractService(config: HttpClientConfig): ContractService {
  return {
    async listContracts(): Promise<Contract[]> {
      const env = await httpRequestWithRefresh<ListContractsResponseDto>(
        config,
        'GET',
        PATH.adminContracts,
      );
      return mapListContractsResponseDto(unwrapResult(env, 'contract.list'));
    },


    async createContract(req: CreateContractRequest): Promise<Contract> {
      const env = await httpRequestWithRefresh<ContractDto>(
        config,
        'POST',
        PATH.adminContracts,
        createContractRequestToDto(req),
      );
      return mapContractDto(unwrapResult(env, 'contract.create'));
    },

    async updateContract(systemId: string, req: UpdateContractRequest): Promise<Contract> {
      const env = await httpRequestWithRefresh<ContractDto>(
        config,
        'PATCH',
        PATH.adminContract(systemId),
        updateContractRequestToDto(req),
      );
      return mapContractDto(unwrapResult(env, 'contract.update'));
    },

    async getMyContract(): Promise<Contract> {
      const env = await httpRequestWithRefresh<ContractDto>(config, 'GET', PATH.mine);
      return mapContractDto(unwrapResult(env, 'contract.mine'));
    },

    async addSeat(req: SeatMutationRequest): Promise<Contract> {
      const env = await httpRequestWithRefresh<ContractDto>(
        config,
        'POST',
        PATH.mineSeats,
        seatMutationRequestToDto(req),
      );
      return mapContractDto(unwrapResult(env, 'contract.addSeat'));
    },

    async removeSeat(req: SeatMutationRequest): Promise<Contract> {
      const env = await httpRequestWithRefresh<ContractDto>(
        config,
        'DELETE',
        PATH.mineSeats,
        seatMutationRequestToDto(req),
      );
      return mapContractDto(unwrapResult(env, 'contract.removeSeat'));
    },
  };
}
